import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  IconButton, 
  Button,
  CircularProgress,
  Alert,
  Slider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';
import { 
  ChevronLeft as ChevronLeftIcon, 
  ChevronRight as ChevronRightIcon 
} from '@mui/icons-material';
import { 
  format, 
  addMonths, 
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  isSameMonth,
  startOfWeek,
  endOfWeek,
  isToday,
  isPast,
  isWithinInterval,
  parseISO,
  getDay,
  parse,
  addHours
} from 'date-fns';
import { TimeSlot, TimeRanges } from '../models/types';
import { PricingRule } from '../models/types';
import { getAvailabilityByDate } from '../firebase/services/availabilityService';
import { getVesselsByType } from '../firebase/services/vesselService';
import { getReservationsByDateRange } from '../firebase/services/reservationService';
import { AvailabilityBlock, Reservation } from '../models/types';
import { getMarinaSettings } from '../firebase/services/marinaSettingsService';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getPricingRules } from '../firebase/services/pricingService';
import { formatTime12Hour } from '../utils/formatters';
import { useAuth } from '../contexts/AuthContext';

interface AvailabilityCalendarProps {
  vesselType: string;
  onDateSelect: (date: Date) => void;
  onTimeSlotSelect: (timeSlot: TimeSlot, price?: number, duration?: number) => void;
  selectedVesselId?: string;
  showPrices?: boolean;
}

interface DayAvailability {
  date: Date;
  isAvailable: boolean;
  availableTimeSlots: TimeSlot[];
}

// Function to convert time string to minutes
const convertTimeToMinutes = (timeStr: string): number => {
  if (timeStr === 'closed') return 0;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

// Function to create a custom time slot based on start time and duration
const createCustomTimeSlot = (startTime: string, duration: number): string => {
  const [startHour, startMinute] = startTime.split(':').map(Number);
  let endHour = startHour + Math.floor(duration);
  let endMinute = startMinute + ((duration % 1) * 60);
  
  // Handle minute overflow
  if (endMinute >= 60) {
    endHour += 1;
    endMinute -= 60;
  }
  
  // Format the times as HH:MM
  const formattedStartTime = `${startHour.toString().padStart(2, '0')}:${startMinute.toString().padStart(2, '0')}`;
  const formattedEndTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
  
  return `${formattedStartTime}-${formattedEndTime}`;
};

// Function to calculate time slot ranges based on business hours
const calculateTimeSlotRanges = (openTime: string, closeTime: string, forDate: Date, marinaSettings?: any): { availableTimeSlots: string[] } => {
  const openTimeMinutes = convertTimeToMinutes(openTime);
  const closeTimeMinutes = convertTimeToMinutes(closeTime);
  
  // Generate time slots for different durations
  const availableTimeSlots: string[] = [];
  
  // Check if this is for today's date
  const today = new Date();
  const isToday = forDate.toDateString() === today.toDateString();
  
  // Get minimum advance booking time from marina settings
  const minAdvanceHours = (marinaSettings as any)?.minAdvanceBooking || 2;
  
  let actualStartTime = openTime;
  let actualStartMinutes = openTimeMinutes;
  
  if (isToday) {
    // Calculate minimum booking time (current time + advance booking hours)
    const minBookingTime = new Date();
    minBookingTime.setHours(minBookingTime.getHours() + minAdvanceHours);
    
    // Convert to minutes for comparison
    const minBookingMinutes = minBookingTime.getHours() * 60 + minBookingTime.getMinutes();
    
    // If minimum booking time is after opening time, use that as start time
    if (minBookingMinutes > openTimeMinutes) {
      // Round up to the next hour to ensure we don't show partial slots
      const minBookingHour = Math.ceil(minBookingMinutes / 60);
      
      actualStartMinutes = minBookingHour * 60;
      actualStartTime = `${minBookingHour.toString().padStart(2, '0')}:00`;
      
      console.log('Adjusted start time for today:', actualStartTime, 'original min booking:', minBookingTime.toLocaleTimeString());
    }
  }
  
  // Calculate remaining business time
  const remainingBusinessTime = closeTimeMinutes - actualStartMinutes;
  
  // If there's not enough time left for any meaningful slot, return empty array
  if (remainingBusinessTime < 180) { // Less than 3 hours
    console.log('Not enough time remaining for any slots:', remainingBusinessTime, 'minutes');
    return { availableTimeSlots: [] };
  }
  
  // Add a 3-hour time slot if there's enough time
  if (remainingBusinessTime >= 180) { // 3 hours = 180 minutes
    availableTimeSlots.push(createCustomTimeSlot(actualStartTime, 3));
  }
  
  // Add a full-day time slot if at least 7 hours are available
  if (remainingBusinessTime >= 420) { // 7 hours = 420 minutes
    availableTimeSlots.push(createCustomTimeSlot(actualStartTime, 7));
  }
  
  console.log('Available time slots for', forDate.toDateString(), ':', availableTimeSlots);
  console.log('Start time:', actualStartTime, 'Remaining business time:', remainingBusinessTime, 'minutes');
  
  return { availableTimeSlots };
};

// Add the parseTimeString function
const parseTimeString = (timeStr: string): Date => {
  const [time, period] = timeStr.split(' ');
  const [hours, minutes] = time.split(':').map(Number);
  const date = new Date();
  date.setHours(period === 'PM' ? hours + 12 : hours, minutes, 0, 0);
  return date;
};

// Add the formatTime function
const formatTime = (date: Date): string => {
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
};

const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  vesselType,
  onDateSelect,
  onTimeSlotSelect,
  selectedVesselId,
  showPrices = true
}) => {
  // Use current date as initial month, availability checking will find the next available month if needed
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availabilityData, setAvailabilityData] = useState<DayAvailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [marinaSettingsState, setMarinaSettingsState] = useState<any>(null);
  
  // New state for custom time selection
  const [selectedStartTime, setSelectedStartTime] = useState<Date | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number>(1);
  const [availableStartTime, setAvailableStartTime] = useState('');
  const [availableEndTime, setAvailableEndTime] = useState('');
  const [timeError, setTimeError] = useState<string | null>(null);
  const [currentDayHours, setCurrentDayHours] = useState<{openTime: string, closeTime: string}>({openTime: '', closeTime: ''});

  // Add state to track whether we've already attempted to find an available month
  const [searchedForAvailableMonth, setSearchedForAvailableMonth] = useState(false);

  // Add state for pricing rules
  const [pricingRules, setPricingRules] = useState<PricingRule[]>([]);

  const { currentUser } = useAuth();

  const calendarGridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (calendarGridRef.current) {
      calendarGridRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentMonth]);

  // Function to get seasonal dates for a specific day
  const getSeasonalDatesForDay = (checkDay: Date, settings?: any): { start: Date | null, end: Date | null, isInSeason: boolean } => {
    let seasonStart: Date | null = null;
    let seasonEnd: Date | null = null;
    let isInSeason = false;
    
    // If settings aren't provided, return default values
    if (!settings) {
      return { start: null, end: null, isInSeason: true };
    }
    
    const marinaSettings = settings;
    
    if (marinaSettings.isSeasonalHoursEnabled && 
        marinaSettings.seasonStartDate && 
        marinaSettings.seasonEndDate) {
      
      try {
        // Use year from the date being checked
        const yearToUse = checkDay.getFullYear();
        
        // Parse season start date from settings
        const startDateStr = marinaSettings.seasonStartDate;
        if (startDateStr && startDateStr.includes('-')) {
          const parts = startDateStr.split('-');
          if (parts.length === 2) {
            // Format MM-DD, add year from date being checked
            const [month, date] = parts.map(Number);
            seasonStart = new Date(yearToUse, month - 1, date);
          } else if (parts.length === 3) {
            // Format YYYY-MM-DD
            seasonStart = parseISO(startDateStr);
            // Update year if needed
            seasonStart.setFullYear(yearToUse);
          }
        } else if (startDateStr) {
          // Try to parse as a date string
          const parsedDate = new Date(startDateStr);
          if (!isNaN(parsedDate.getTime())) {
            seasonStart = parsedDate;
            seasonStart.setFullYear(yearToUse);
          }
        }
        
        // If still no valid season start, use a reasonable default (January 1st)
        if (!seasonStart || isNaN(seasonStart.getTime())) {
          seasonStart = new Date(yearToUse, 0, 1); // January 1st
          console.warn('Failed to parse season start date, using January 1st as default');
        }
        
        // Parse season end date
        const endDateStr = marinaSettings.seasonEndDate;
        if (endDateStr && endDateStr.includes('-')) {
          const parts = endDateStr.split('-');
          if (parts.length === 2) {
            // Format MM-DD, add year from date being checked
            const [month, date] = parts.map(Number);
            seasonEnd = new Date(yearToUse, month - 1, date);
          } else if (parts.length === 3) {
            // Format YYYY-MM-DD
            seasonEnd = parseISO(endDateStr);
            // Update year if needed
            seasonEnd.setFullYear(yearToUse);
          }
        } else if (endDateStr) {
          // Try to parse as a date string
          const parsedDate = new Date(endDateStr);
          if (!isNaN(parsedDate.getTime())) {
            seasonEnd = parsedDate;
            seasonEnd.setFullYear(yearToUse);
          }
        }
        
        // If still no valid season end, use a reasonable default (December 31st)
        if (!seasonEnd || isNaN(seasonEnd.getTime())) {
          seasonEnd = new Date(yearToUse, 11, 31); // December 31st
          console.warn('Failed to parse season end date, using December 31st as default');
        }

        // Check if the day is within season using simple date comparison
        if (!isNaN(seasonStart.getTime()) && 
            !isNaN(seasonEnd.getTime())) {
          
          // Simple date comparison to check if day is in season
          isInSeason = checkDay >= seasonStart && checkDay <= seasonEnd;
          
          // Debug log the season check for certain days
          if (checkDay.getDate() === 1 || checkDay.getDate() === 15 || checkDay.getDate() === 23) {
            console.log(
              `Seasonal check for ${checkDay.toDateString()}: ` + 
              `season=${seasonStart.toDateString()} to ${seasonEnd.toDateString()}, ` +
              `isInSeason=${isInSeason}, ` +
              `checkDay >= seasonStart: ${checkDay >= seasonStart}, ` +
              `checkDay <= seasonEnd: ${checkDay <= seasonEnd}`
            );
          }
        }
      } catch (error) {
        console.error('Error parsing seasonal dates:', error);
        // Use generic defaults if parsing fails completely
        const yearToUse = checkDay.getFullYear();
        seasonStart = new Date(yearToUse, 0, 1); // January 1st
        seasonEnd = new Date(yearToUse, 11, 31); // December 31st
        isInSeason = checkDay >= seasonStart && checkDay <= seasonEnd;
      }
    } else {
      // If seasonal hours aren't enabled, treat all days as in-season
      isInSeason = true;
    }
    
    return { start: seasonStart, end: seasonEnd, isInSeason };
  };

  // Function to find the next month with available dates
  const findNextMonthWithAvailableDates = async (startingMonth: Date, maxMonths = 6): Promise<Date | null> => {
    let monthToCheck = startingMonth;
    
    // Get marina settings for advance booking window
    const marinaSettings = await getMarinaSettings();
    const { advanceBookingDays } = marinaSettings.reservationRules;
    const currentDate = new Date();
    const advanceLimit = new Date();
    advanceLimit.setDate(currentDate.getDate() + advanceBookingDays);
    
    for (let i = 0; i < maxMonths; i++) {
      if (i > 0) {
        monthToCheck = addMonths(monthToCheck, 1);
      }
      
      // Skip months beyond the advance booking window
      if (monthToCheck > advanceLimit) {
        continue;
      }
      
      const startDate = format(startOfMonth(monthToCheck), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(monthToCheck), 'yyyy-MM-dd');
      
      // Get marina settings for business hours and rules
      const marinaSettings = await getMarinaSettings();
      
      // Get all reservations for the month
      const reservations = await getReservationsByDateRange(
        startDate,
        endDate
      );
      
      // Get all vessels of the specified type
      const vesselsData = await getVesselsByType();
      const vessels = vesselsData[vesselType] || [];
      
      // Create a map of dates to their reservations
      const reservationMap = new Map<string, Map<string, Set<TimeSlot>>>();
      reservations.forEach(reservation => {
        if (!reservationMap.has(reservation.date)) {
          reservationMap.set(reservation.date, new Map<string, Set<TimeSlot>>());
        }
        
        const vesselMap = reservationMap.get(reservation.date)!;
        
        if (!vesselMap.has(reservation.vesselId)) {
          vesselMap.set(reservation.vesselId, new Set<TimeSlot>());
        }
        
        vesselMap.get(reservation.vesselId)!.add(reservation.timeSlot);
      });
      
      // Generate array of days for the month
      const daysInMonth = eachDayOfInterval({ 
        start: startOfMonth(monthToCheck), 
        end: endOfMonth(monthToCheck) 
      });
      
      // Check if any day in the month is available
      for (const day of daysInMonth) {
        // Skip days beyond the advance booking window
        if (day > advanceLimit) {
          continue;
        }
        
        const dateStr = format(day, 'yyyy-MM-dd');
        const dayOfWeek = format(day, 'EEEE').toLowerCase();
        
        // Check if the date is in season (if seasonal hours are enabled)
        let isInSeason = true;
        if (marinaSettings.isSeasonalHoursEnabled && 
            marinaSettings.seasonStartDate && 
            marinaSettings.seasonEndDate) {
          
          const { isInSeason: seasonalCheck } = getSeasonalDatesForDay(day, marinaSettings);
          isInSeason = seasonalCheck;
        }
        
        // Get the appropriate business hours based on season
        const dayHours = isInSeason && marinaSettings.inSeasonHours 
          ? marinaSettings.inSeasonHours[dayOfWeek]
          : (!isInSeason && marinaSettings.outOfSeasonHours 
            ? marinaSettings.outOfSeasonHours[dayOfWeek]
            : marinaSettings.businessHours[dayOfWeek]);
        
        // Check if the marina is open on this day
        const isDayClosed = !dayHours || dayHours.isClosed;
        
        // Skip days when the marina is closed
        if (isDayClosed) {
          continue;
        }
        
        // If no reservations exist for this date, all vessels are available
        if (!reservationMap.has(dateStr)) {
          return monthToCheck; // Month has available days
        }
        
        // Check if at least one vessel is available for at least one time slot
        for (const vessel of vessels) {
          const vesselReservations = reservationMap.get(dateStr)?.get(vessel.id);
          if (!vesselReservations || vesselReservations.size === 0) {
            return monthToCheck; // Month has available days
          }
        }
      }
    }
    
    return null; // No available month found
  };

  // Fetch availability data for the current month
  useEffect(() => {
    const fetchAvailabilityData = async () => {
      if (!vesselType) return;
      
      try {
        setLoading(true);
        const startDate = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
        const endDate = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
        
        // Get marina settings for business hours and rules
        const marinaSettings = await getMarinaSettings();
        setMarinaSettingsState(marinaSettings);
        
        // Get all reservations for the month
        const reservations = await getReservationsByDateRange(
          startDate,
          endDate
        );
        
        // Get all vessels of the specified type
        const vesselsData = await getVesselsByType();
        const vessels = vesselsData[vesselType] || [];
        
        // Create a map of dates to their reservations
        const reservationMap = new Map<string, Map<string, Set<TimeSlot>>>();
        reservations.forEach(reservation => {
          if (!reservationMap.has(reservation.date)) {
            reservationMap.set(reservation.date, new Map<string, Set<TimeSlot>>());
          }
          
          const vesselMap = reservationMap.get(reservation.date)!;
          
          if (!vesselMap.has(reservation.vesselId)) {
            vesselMap.set(reservation.vesselId, new Set<TimeSlot>());
          }
          
          vesselMap.get(reservation.vesselId)!.add(reservation.timeSlot);
        });
        
        // Generate array of days for the month
        const daysInMonth = eachDayOfInterval({ 
          start: startOfMonth(currentMonth), 
          end: endOfMonth(currentMonth) 
        });
        
        // Get advance booking days limit
        const { advanceBookingDays } = marinaSettings.reservationRules;
        const currentDate = new Date();
        const advanceLimit = new Date();
        advanceLimit.setDate(currentDate.getDate() + advanceBookingDays);
        
        const availabilityData = daysInMonth.map(day => {
          const dateStr = format(day, 'yyyy-MM-dd');
          const dayOfWeek = format(day, 'EEEE').toLowerCase();
          
          // Check if the date is in season (if seasonal hours are enabled)
          let isInSeason = true;
          if (marinaSettings.isSeasonalHoursEnabled && 
              marinaSettings.seasonStartDate && 
              marinaSettings.seasonEndDate) {
            
            const { isInSeason: seasonalCheck } = getSeasonalDatesForDay(day, marinaSettings);
            isInSeason = seasonalCheck;
          }
          
          // Get the appropriate business hours based on season
          const dayHours = isInSeason && marinaSettings.inSeasonHours 
            ? marinaSettings.inSeasonHours[dayOfWeek]
            : (!isInSeason && marinaSettings.outOfSeasonHours 
              ? marinaSettings.outOfSeasonHours[dayOfWeek]
              : marinaSettings.businessHours[dayOfWeek]);
          
          // Check if the marina is open on this day
          const isDayClosed = !dayHours || dayHours.isClosed;
          
          // Check if the date is within the advance booking window
          const isBeyondAdvanceBooking = day > advanceLimit;
          
          // Initialize available time slots
          const availableTimeSlots: TimeSlot[] = [];
          
          // Check if today has any available time for custom bookings
          let isAvailableForCustomBooking = false;
          const today = new Date();
          const isToday = day.toDateString() === today.toDateString();
          
          // If the marina is open and in season, check for available time slots
          if (!isDayClosed && isInSeason && !isBeyondAdvanceBooking) {
            // Calculate time slot ranges based on business hours
            const { availableTimeSlots: generatedTimeSlots } = calculateTimeSlotRanges(dayHours.openTime, dayHours.closeTime, day, marinaSettings);
            
            // Add the generated time slots to available slots
            generatedTimeSlots.forEach(slot => {
              // Check if any vessel is available for this date and slot
              const dateReservations = reservationMap.get(dateStr);
              
              // If no reservations exist for this date, all vessels are available
              if (!dateReservations) {
                availableTimeSlots.push(slot);
                return;
              }
              
              // Check if at least one vessel is available (not reserved) for this slot
              const isAnyVesselAvailable = vessels.some(vessel => {
                const vesselReservations = dateReservations.get(vessel.id);
                return !vesselReservations || !vesselReservations.has(slot);
              });
              
              if (isAnyVesselAvailable) {
                availableTimeSlots.push(slot);
              }
            });
            
            // For today's date, also check if there's time available for custom bookings
            if (isToday && availableTimeSlots.length === 0) {
              // Get minimum advance booking time from marina settings
              const minAdvanceHours = (marinaSettings as any)?.minAdvanceBooking || 2;
              
              // Calculate minimum booking time (current time + advance booking hours)
              const minBookingTime = new Date();
              minBookingTime.setHours(minBookingTime.getHours() + minAdvanceHours);
              
              // Check if there's still time left in the business day
              const [closeHour, closeMinute] = dayHours.closeTime.split(':').map(Number);
              const closeTime = new Date(day);
              closeTime.setHours(closeHour, closeMinute, 0, 0);
              
              // If minimum booking time is before closing time, custom booking is available
              if (minBookingTime < closeTime) {
                isAvailableForCustomBooking = true;
              }
            }
            
            console.log(`Date ${format(day, 'yyyy-MM-dd')} marked available with ${availableTimeSlots.length} time slots`);
          }
          
          return {
            date: day,
            isAvailable: availableTimeSlots.length > 0 || isAvailableForCustomBooking,
            availableTimeSlots
          };
        });
        
        setAvailabilityData(availabilityData);
        
        // Check if any days in the current month are available
        const hasAvailableDays = availabilityData.some(day => day.isAvailable);
        
        // If no available days in current month and we haven't searched yet, try to find next available month
        if (!hasAvailableDays && !searchedForAvailableMonth && isSameMonth(currentMonth, new Date())) {
          console.log("No available days in current month, searching for next available month");
          setSearchedForAvailableMonth(true);
          
          const nextAvailableMonth = await findNextMonthWithAvailableDates(currentMonth);
          if (nextAvailableMonth) {
            console.log(`Found available days in ${format(nextAvailableMonth, 'MMMM yyyy')}`);
            setCurrentMonth(nextAvailableMonth);
          } else {
            console.log("No available days found in the next 6 months");
          }
        }
      } catch (err) {
        console.error('Error fetching availability data:', err);
        setError('Failed to load availability data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAvailabilityData();
  }, [currentMonth, vesselType, searchedForAvailableMonth]);

  // Set available start and end times based on business hours for the selected date
  useEffect(() => {
    if (!selectedDate || !marinaSettingsState) return;
    
    // Check if the selected date is in season
    const { isInSeason } = getSeasonalDatesForDay(selectedDate, marinaSettingsState);
    
    // Get business hours for the selected date
    const calendarDayOfWeek = format(selectedDate, 'EEEE').toLowerCase();
    
    let businessHours = marinaSettingsState?.businessHours || {
      [calendarDayOfWeek]: { openTime: '', closeTime: '' }
    };
    
    if (marinaSettingsState?.isSeasonalHoursEnabled) {
      businessHours = isInSeason
        ? (marinaSettingsState.inSeasonHours || marinaSettingsState.businessHours)
        : (marinaSettingsState.outOfSeasonHours || marinaSettingsState.businessHours);
    }
    
    const dayHours = businessHours[calendarDayOfWeek as keyof typeof businessHours];
    setCurrentDayHours(dayHours);
    
    if (dayHours) {
      setAvailableStartTime(dayHours.openTime);
      setAvailableEndTime(dayHours.closeTime);
      
      // Set default start time based on the date
      if (dayHours.openTime !== 'closed') {
        const today = new Date();
        const isToday = selectedDate.toDateString() === today.toDateString();
        
        if (isToday) {
          // For today, calculate the next available time
          const minAdvanceHours = (marinaSettingsState as any)?.minAdvanceBooking || 2;
          const minBookingTime = new Date();
          minBookingTime.setHours(minBookingTime.getHours() + minAdvanceHours);
          
          // Round up to the next full hour for cleaner time slots
          const minutes = minBookingTime.getMinutes();
          if (minutes > 0) {
            minBookingTime.setHours(minBookingTime.getHours() + 1, 0, 0, 0);
          }
          
          // Check if this time is within business hours
          const [closeHour, closeMinute] = dayHours.closeTime.split(':').map(Number);
          const closeTime = new Date(selectedDate);
          closeTime.setHours(closeHour, closeMinute, 0, 0);
          
          if (minBookingTime < closeTime) {
            setSelectedStartTime(minBookingTime);
            console.log('Default start time set for today:', minBookingTime.toLocaleTimeString());
          } else {
            // No time available today
            setSelectedStartTime(null);
            console.log('No time available today - closing time:', closeTime.toLocaleTimeString(), 'min booking time:', minBookingTime.toLocaleTimeString());
          }
        } else {
          // For future dates, use opening time
          const [hours, minutes] = dayHours.openTime.split(':').map(Number);
          const defaultStartTime = new Date(selectedDate);
          defaultStartTime.setHours(hours, minutes, 0);
          setSelectedStartTime(defaultStartTime);
        }
      }
    }
  }, [selectedDate, marinaSettingsState]);

  // Add useEffect to fetch pricing rules
  useEffect(() => {
    const fetchPricingRules = async () => {
      try {
        console.log('Fetching pricing rules for vessel type:', vesselType);
        const rules = await getPricingRules();
        console.log('Fetched pricing rules:', rules);
        setPricingRules(rules);
      } catch (error) {
        console.error('Error fetching pricing rules:', error);
      }
    };
    fetchPricingRules();
  }, [vesselType]);

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
    setSelectedDate(null);
    setShowTimeSlots(false);
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
    setSelectedDate(null);
    setShowTimeSlots(false);
  };

  const handleDateClick = (date: Date) => {
    if (isPast(date) && !isToday(date)) {
      return; // Don't allow selecting past dates
    }
    
    // Get the seasonal info for this day
    const { isInSeason } = marinaSettingsState 
      ? getSeasonalDatesForDay(date, marinaSettingsState) 
      : { isInSeason: false };
    
    // If the date is in season, allow selection regardless of availability
    if (isInSeason) {
      setSelectedDate(date);
      // Call onDateSelect here to ensure the parent component updates its state immediately
      onDateSelect(date);
      setShowTimeSlots(true);
      return;
    }
    
    // For non-in-season dates, check regular availability
    const dayAvailability = availabilityData.find(day => isSameDay(day.date, date));
    
    if (dayAvailability && dayAvailability.isAvailable) {
      setSelectedDate(date);
      // Call onDateSelect here to ensure the parent component updates its state immediately
      onDateSelect(date);
      setShowTimeSlots(true);
    }
  };

  const handleTimeSlotClick = (timeSlot: TimeSlot) => {
    // We've already called onDateSelect in handleDateClick, so the parent's selectedDate should be set
    // Now we just need to call onTimeSlotSelect
    onTimeSlotSelect(timeSlot);
  };

  // Function to handle custom time selection
  const handleStartTimeChange = (newTime: Date | null) => {
    setSelectedStartTime(newTime);
    setTimeError(null);
    
    // Automatically confirm when time changes if duration is set
    if (newTime && selectedDuration > 0) {
      validateAndConfirmTimeSlot(newTime, selectedDuration);
    }
  };
  
  // Function to handle duration change
  const handleDurationChange = (event: any) => {
    const newDuration = event.target.value;
    setSelectedDuration(newDuration);
    
    // Automatically confirm when duration changes if start time is set
    if (selectedStartTime && newDuration > 0) {
      validateAndConfirmTimeSlot(selectedStartTime, newDuration);
    }
  };

  // Convert 24-hour time format to 12-hour format
  const convertTo12HourFormat = (timeStr: string) => {
    return formatTime12Hour(timeStr);
  };

  // Validate and confirm the time slot selection
  const validateAndConfirmTimeSlot = (startTime: Date, duration: number) => {
    if (!selectedDate || !marinaSettingsState) return;
    
    const endTime = addHours(startTime, duration);
    
    // Check if this is for today's date
    const today = new Date();
    const isToday = selectedDate.toDateString() === today.toDateString();
    
    if (isToday) {
      // Get minimum advance booking time from marina settings
      const minAdvanceHours = (marinaSettingsState as any)?.minAdvanceBooking || 2;
      
      // Calculate minimum booking time (current time + advance booking hours)
      const minBookingTime = new Date();
      minBookingTime.setHours(minBookingTime.getHours() + minAdvanceHours);
      
      // Check if the selected start time is before the minimum booking time
      if (startTime < minBookingTime) {
        setTimeError(`Reservations must be made at least ${minAdvanceHours} hours in advance. Please select a time after ${format(minBookingTime, 'h:mm a')}`);
        return;
      }
    }
    
    // Validate the selected time is within business hours
    const startHour = startTime.getHours();
    const startMinutes = startTime.getMinutes();
    const endHour = endTime.getHours();
    const endMinutes = endTime.getMinutes();
    
    const [openHour, openMinute] = availableStartTime.split(':').map(Number);
    const [closeHour, closeMinute] = availableEndTime.split(':').map(Number);
    
    const startTimeInMinutes = startHour * 60 + startMinutes;
    const endTimeInMinutes = endHour * 60 + endMinutes;
    const openTimeInMinutes = openHour * 60 + openMinute;
    const closeTimeInMinutes = closeHour * 60 + closeMinute;
    
    if (startTimeInMinutes < openTimeInMinutes || endTimeInMinutes > closeTimeInMinutes) {
      setTimeError(`Please select a time between ${formatTime12Hour(availableStartTime)} and ${formatTime12Hour(availableEndTime)}`);
      return;
    }
    
    // Clear any previous errors
    setTimeError(null);
  };
  
  // Function to handle custom time slot confirmation
  const handleCustomTimeSlotConfirm = () => {
    if (!selectedStartTime || !selectedDate || !marinaSettingsState) {
      setTimeError('Please select a start time');
      return;
    }
    
    const endTime = calculateEndTime();
    if (!endTime) return;
    
    // Check if this is for today's date
    const today = new Date();
    const isToday = selectedDate.toDateString() === today.toDateString();
    
    if (isToday) {
      // Get minimum advance booking time from marina settings
      const minAdvanceHours = (marinaSettingsState as any)?.minAdvanceBooking || 2;
      
      // Calculate minimum booking time (current time + advance booking hours)
      const minBookingTime = new Date();
      minBookingTime.setHours(minBookingTime.getHours() + minAdvanceHours);
      
      // Check if the selected start time is before the minimum booking time
      if (selectedStartTime < minBookingTime) {
        setTimeError(`Reservations must be made at least ${minAdvanceHours} hours in advance. Please select a time after ${format(minBookingTime, 'h:mm a')}`);
        return;
      }
    }
    
    // Validate the selected time is within business hours
    const startHour = selectedStartTime.getHours();
    const startMinutes = selectedStartTime.getMinutes();
    const endHour = endTime.getHours();
    const endMinutes = endTime.getMinutes();
    
    const [openHour, openMinute] = availableStartTime.split(':').map(Number);
    const [closeHour, closeMinute] = availableEndTime.split(':').map(Number);
    
    const startTimeInMinutes = startHour * 60 + startMinutes;
    const endTimeInMinutes = endHour * 60 + endMinutes;
    const openTimeInMinutes = openHour * 60 + openMinute;
    const closeTimeInMinutes = closeHour * 60 + closeMinute;
    
    if (startTimeInMinutes < openTimeInMinutes || endTimeInMinutes > closeTimeInMinutes) {
      setTimeError(`Please select a time between ${formatTime12Hour(availableStartTime)} and ${formatTime12Hour(availableEndTime)}`);
      return;
    }
    
    // Get the price for this time slot
    const priceDetails = calculatePriceForDuration(selectedDuration);
    if (!priceDetails) {
      setTimeError('Unable to calculate price for this time slot');
      return;
    }
    
    // Create a custom time slot with the format "HH:MM-HH:MM"
    const formattedStartTime = format(selectedStartTime, 'HH:mm');
    const formattedEndTime = format(endTime, 'HH:mm');
    const customTimeSlot = `${formattedStartTime}-${formattedEndTime}` as TimeSlot;
    
    // Show a brief confirmation message to indicate the selection was made
    setTimeError(null);
    
    // Call the parent component callbacks
    if (selectedDate) {
      console.log('Applying time slot:', customTimeSlot, 'with price:', priceDetails.totalPrice);
      onDateSelect(selectedDate);
      onTimeSlotSelect(customTimeSlot, priceDetails.totalPrice, selectedDuration);
    }
  };

  // Calculate end time based on start time and duration
  const calculateEndTime = (): Date | null => {
    if (!selectedStartTime) return null;
    return addHours(selectedStartTime, selectedDuration);
  };

  // Update calculatePriceForDuration function
  const calculatePriceForDuration = (duration: number) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('=== PRICING CALCULATION DEBUG ===');
      console.log('Calculating price for duration:', duration, 'vessel type:', vesselType);
      console.log('Selected date:', selectedDate);
      console.log('Available pricing rules:', pricingRules);
    }
    
    if (!pricingRules.length) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('No pricing rules available, using fallback');
      }
      // Return a default price based on marina settings if no pricing rules exist
      const defaultBaseRate = marinaSettingsState?.reservationRules?.baseRate || 100;
      if (process.env.NODE_ENV === 'development') {
        console.log('Fallback base rate from marina settings:', defaultBaseRate);
      }
      return {
        basePrice: defaultBaseRate,
        timeBlockPrice: defaultBaseRate * duration,
        totalPrice: defaultBaseRate * duration
      };
    }
    
    // Find the applicable pricing rule for this vessel type
    const applicableRule = pricingRules.find(rule => {
      if (process.env.NODE_ENV === 'development') {
        console.log('Checking rule:', rule);
        console.log('Rule vessel type:', rule.vesselType, 'vs', vesselType);
        console.log('Rule vessel type match:', rule.vesselType === vesselType);
      }
      const vesselTypeMatch = rule.vesselType === vesselType;
      const startDateMatch = !rule.startDate || new Date(rule.startDate) <= selectedDate!;
      const endDateMatch = !rule.endDate || new Date(rule.endDate) >= selectedDate!;
      if (process.env.NODE_ENV === 'development') {
        console.log('Start date match:', startDateMatch);
        console.log('End date match:', endDateMatch);
        console.log('Overall match:', vesselTypeMatch && startDateMatch && endDateMatch);
      }
      return vesselTypeMatch && startDateMatch && endDateMatch;
    });
    
    if (!applicableRule) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('No applicable pricing rule found for vessel type:', vesselType);
        console.warn('Using fallback pricing');
      }
      // Return a default price based on marina settings if no matching rule
      const defaultBaseRate = marinaSettingsState?.reservationRules?.baseRate || 100;
      return {
        basePrice: defaultBaseRate,
        timeBlockPrice: defaultBaseRate * duration,
        totalPrice: defaultBaseRate * duration
      };
    }
    
    const basePrice = applicableRule.basePrice || 0;
    const timeBlockPrices = applicableRule.timeBlockPrices || [];
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Found applicable rule:', applicableRule);
      console.log('Time block prices:', timeBlockPrices);
    }
    
    // Find the applicable time block for this duration
    const applicableTimeBlock = timeBlockPrices
      .sort((a, b) => b.hours - a.hours) // Sort in descending order
      .find(block => {
        if (process.env.NODE_ENV === 'development') {
          console.log('Checking time block:', block, 'for duration:', duration);
        }
        return duration >= block.hours;
      });
    
    if (!applicableTimeBlock) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('No applicable time block found for duration:', duration);
        console.warn('Available time blocks:', timeBlockPrices);
      }
      // If no time block matches, calculate based on base price
      return {
        basePrice: basePrice,
        timeBlockPrice: basePrice * duration,
        totalPrice: basePrice * duration
      };
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Using time block:', applicableTimeBlock);
      console.log('Final price:', applicableTimeBlock.price);
      console.log('=== END PRICING CALCULATION ===');
    }
    
    // Simply return the time block price as the total price
    return {
      basePrice: 0, // We're not using base price separately
      timeBlockPrice: applicableTimeBlock.price,
      totalPrice: applicableTimeBlock.price // Just use the time block price directly
    };
  };

  // Replace the handleConfirmReservation function
  const handleConfirmReservation = async (selectedDate: Date, selectedTimeSlot: TimeSlot) => {
    // Parse the time slot to get start and end times
    const [startTimeStr, endTimeStr] = selectedTimeSlot.split('-');
    
    // Calculate price based on duration
    const marinaSettings = await getMarinaSettings();
    const baseRate = marinaSettings?.reservationRules?.baseRate || 0;
    
    // Calculate duration in hours
    const startHour = parseInt(startTimeStr.split(':')[0]);
    const startMinute = parseInt(startTimeStr.split(':')[1]);
    const endHour = parseInt(endTimeStr.split(':')[0]);
    const endMinute = parseInt(endTimeStr.split(':')[1]);
    
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    const durationHours = (endMinutes - startMinutes) / 60;
    
    // Calculate price (simplified example)
    const calculatedPrice = baseRate * durationHours;
    
    onTimeSlotSelect(selectedTimeSlot, calculatedPrice, durationHours);
  };

  const renderCalendar = () => {
    const firstDayOfMonth = startOfMonth(currentMonth);
    const lastDayOfMonth = endOfMonth(currentMonth);
    const startDay = startOfWeek(firstDayOfMonth, { weekStartsOn: 0 }); // Start week on Sunday
    const endDay = endOfWeek(lastDayOfMonth, { weekStartsOn: 0 }); // End week on Saturday
    
    const days = eachDayOfInterval({ start: startDay, end: endDay });

    // Get advance booking days limit
    const { advanceBookingDays } = marinaSettingsState?.reservationRules || { advanceBookingDays: 30 };
    const currentDate = new Date();
    const advanceLimit = new Date();
    advanceLimit.setDate(currentDate.getDate() + advanceBookingDays);

    // Generate list of months for dropdown (current month + next 5 months)
    const monthOptions = [];
    const today = new Date();
    for (let i = 0; i < 6; i++) {
      const monthDate = addMonths(today, i);
      // Only include months that are within the advance booking window
      if (monthDate <= advanceLimit) {
        monthOptions.push({
          value: monthDate,
          label: format(monthDate, 'MMMM yyyy')
        });
      }
    }
    
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handlePrevMonth} disabled={isPast(startOfMonth(currentMonth)) && !isSameMonth(currentMonth, new Date())}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6" component="h2">
            {format(currentMonth, 'MMMM yyyy')}
          </Typography>
          <IconButton onClick={handleNextMonth}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Box ref={calendarGridRef}>
          <Grid container spacing={1} justifyContent="center">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <Grid key={day} item xs={12/7}>
                <Typography variant="subtitle2" align="center">
                  {day}
                </Typography>
              </Grid>
            ))}
            
            {days.map((day, index) => {
              const dayAvailability = availabilityData.find(d => isSameDay(d.date, day));
              const isAvailable = dayAvailability?.isAvailable ?? false;
              const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
              const isCurrentDay = isToday(day);
              const isPastDay = isPast(day) && !isCurrentDay;
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isBeyondAdvanceBooking = day > advanceLimit;
              
              // Skip rendering days that are not in the current month
              if (!isCurrentMonth) {
                return <Grid key={index} item xs={12/7} />;
              }
              
              // Determine availability status
              let availabilityStatus = 'unavailable';
              const availableTimeSlots = dayAvailability?.availableTimeSlots || [];
              
              if (isAvailable && availableTimeSlots.length > 0 && !isBeyondAdvanceBooking) {
                availabilityStatus = 'available';
              }
              
              // Find the seasonal info for this day
              const { isInSeason } = marinaSettingsState 
                ? getSeasonalDatesForDay(day, marinaSettingsState) 
                : { isInSeason: false };
              
              return (
                <Grid key={index} item xs={12/7}>
                  <Paper 
                    elevation={isSelected ? 3 : 1}
                    sx={{ 
                      width: '100%',
                      aspectRatio: '1.7 / 1',
                      minHeight: 28,
                      maxWidth: '100%',
                      display: 'flex', 
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: (isAvailable || isInSeason) && !isPastDay && !isBeyondAdvanceBooking ? 'pointer' : 'default',
                      backgroundColor: isSelected 
                        ? 'primary.light' 
                        : isPastDay || isBeyondAdvanceBooking
                          ? 'grey.300' 
                          : isAvailable
                            ? 'success.light'
                            : 'error.light',
                      color: isSelected || isAvailable ? 'text.primary' : 'text.secondary',
                      '&:hover': {
                        backgroundColor: (isAvailable || isInSeason) && !isPastDay && !isBeyondAdvanceBooking ? 'primary.main' : undefined,
                        color: (isAvailable || isInSeason) && !isPastDay && !isBeyondAdvanceBooking ? 'white' : undefined
                      },
                      textAlign: 'center',
                      p: 0.5
                    }}
                    onClick={() => (isAvailable || isInSeason) && !isPastDay && !isBeyondAdvanceBooking && handleDateClick(day)}
                  >
                    <Typography variant="body2">
                      {format(day, 'd')}
                    </Typography>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
    );
  };

  const renderTimeSlots = () => {
    if (!selectedDate) return null;
    
    // Get current day hours
    const dayOfWeek = format(selectedDate, 'EEEE').toLowerCase();
    const { isInSeason } = getSeasonalDatesForDay(selectedDate, marinaSettingsState);
    
    let businessHours = marinaSettingsState?.businessHours || {};
    if (marinaSettingsState?.isSeasonalHoursEnabled) {
      businessHours = isInSeason
        ? (marinaSettingsState.inSeasonHours || marinaSettingsState.businessHours)
        : (marinaSettingsState.outOfSeasonHours || marinaSettingsState.businessHours);
    }
    
    const currentDayHours = businessHours[dayOfWeek] || { openTime: '09:00', closeTime: '17:00' };
    
    // Generate default start time based on availability rules
    const getDefaultStartTime = () => {
      if (!selectedDate || !marinaSettingsState) return null;
      
      // Check if this is for today's date
      const today = new Date();
      const isToday = selectedDate.toDateString() === today.toDateString();
      
      if (isToday) {
        // Get minimum advance booking time from marina settings
        const minAdvanceHours = (marinaSettingsState as any)?.minAdvanceBooking || 2;
        
        // Calculate minimum booking time (current time + advance booking hours)
        const minBookingTime = new Date();
        minBookingTime.setHours(minBookingTime.getHours() + minAdvanceHours);
        
        // Round up to the next 30-minute interval
        const minutes = minBookingTime.getMinutes();
        if (minutes <= 30) {
          minBookingTime.setMinutes(30, 0, 0);
        } else {
          minBookingTime.setHours(minBookingTime.getHours() + 1, 0, 0, 0);
        }
        
        // Ensure it's within business hours
        const dayOfWeek = format(selectedDate, 'EEEE').toLowerCase();
        const { isInSeason } = getSeasonalDatesForDay(selectedDate, marinaSettingsState);
        
        let businessHours = marinaSettingsState?.businessHours || {};
        if (marinaSettingsState?.isSeasonalHoursEnabled) {
          businessHours = isInSeason
            ? (marinaSettingsState.inSeasonHours || marinaSettingsState.businessHours)
            : (marinaSettingsState.outOfSeasonHours || marinaSettingsState.businessHours);
        }
        
        const dayHours = businessHours[dayOfWeek];
        if (dayHours && dayHours.closeTime) {
          const [closeHour, closeMinute] = dayHours.closeTime.split(':').map(Number);
          const closeTime = new Date(selectedDate);
          closeTime.setHours(closeHour, closeMinute, 0, 0);
          
          // If calculated time is after closing time, return null (no available time today)
          if (minBookingTime >= closeTime) {
            return null;
          }
        }
        
        return minBookingTime;
      } else {
        // For future dates, default to opening time
        const dayOfWeek = format(selectedDate, 'EEEE').toLowerCase();
        const { isInSeason } = getSeasonalDatesForDay(selectedDate, marinaSettingsState);
        
        let businessHours = marinaSettingsState?.businessHours || {};
        if (marinaSettingsState?.isSeasonalHoursEnabled) {
          businessHours = isInSeason
            ? (marinaSettingsState.inSeasonHours || marinaSettingsState.businessHours)
            : (marinaSettingsState.outOfSeasonHours || marinaSettingsState.businessHours);
        }
        
        const dayHours = businessHours[dayOfWeek];
        if (dayHours && dayHours.openTime !== 'closed') {
          const [hours, minutes] = dayHours.openTime.split(':').map(Number);
          const defaultTime = new Date(selectedDate);
          defaultTime.setHours(hours, minutes, 0);
          return defaultTime;
        }
      }
      
      return null;
    };
    
    // Get min/max duration from marina settings
    const minDuration = marinaSettingsState?.reservationRules?.minReservationDuration || 1;
    const maxDuration = marinaSettingsState?.reservationRules?.maxReservationDuration || 8;
    
    // Generate duration options
    const durationOptions = [];
    for (let i = minDuration; i <= maxDuration; i++) {
      durationOptions.push(i);
    }
    
    // Ensure selectedDuration is a valid option
    if (!durationOptions.includes(selectedDuration)) {
      setSelectedDuration(minDuration);
    }
    
    // Calculate price for a specific duration
    const currentPriceDetails = calculatePriceForDuration(selectedDuration);
    
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Select Reservation Time for {format(selectedDate, 'MMMM d, yyyy')}
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            After selecting your desired time and duration, click "Check Availability" to confirm. 
            If your selected time slot is not available, you'll be informed and can select a different time.
          </Alert>
          
          {timeError && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setTimeError(null)}>
              {timeError}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ width: '100%', mb: 2 }}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={selectedStartTime ? `${selectedStartTime.getHours().toString().padStart(2, '0')}:${selectedStartTime.getMinutes().toString().padStart(2, '0')}` : ''}
                  onChange={e => {
                    const [hours, minutes] = e.target.value.split(":").map(Number);
                    if (selectedDate) {
                      const newDate = new Date(selectedDate);
                      newDate.setHours(hours, minutes, 0, 0);
                      
                      // Validate that the selected time meets advance booking requirements
                      const now = new Date();
                      const isToday = selectedDate.toDateString() === now.toDateString();
                      
                      if (isToday) {
                        const minAdvanceHours = (marinaSettingsState as any)?.minAdvanceBooking || 2;
                        const minBookingTime = new Date();
                        minBookingTime.setHours(minBookingTime.getHours() + minAdvanceHours);
                        
                        if (newDate <= minBookingTime) {
                          setTimeError(`Reservations must be made at least ${minAdvanceHours} ${minAdvanceHours === 1 ? 'hour' : 'hours'} in advance. Please select a time after ${format(minBookingTime, 'h:mm a')}`);
                          return;
                        }
                      }
                      
                      // Validate that the time is within business hours
                      const [openHour, openMinute] = currentDayHours.openTime.split(':').map(Number);
                      const [closeHour, closeMinute] = currentDayHours.closeTime.split(':').map(Number);
                      
                      const openTime = new Date(selectedDate);
                      openTime.setHours(openHour, openMinute, 0, 0);
                      
                      const closeTime = new Date(selectedDate);
                      closeTime.setHours(closeHour, closeMinute, 0, 0);
                      
                      if (newDate < openTime || newDate >= closeTime) {
                        setTimeError(`Please select a time between ${convertTo12HourFormat(currentDayHours.openTime)} and ${convertTo12HourFormat(currentDayHours.closeTime)}`);
                        return;
                      }
                      
                      // Check that the booking won't extend past closing time
                      const endTime = new Date(newDate);
                      endTime.setHours(endTime.getHours() + selectedDuration);
                      
                      if (endTime > closeTime) {
                        setTimeError(`This time slot would extend past closing time. Please select an earlier time or shorter duration.`);
                        return;
                      }
                      
                      // Clear any previous errors if validation passes
                      setTimeError(null);
                      setSelectedStartTime(newDate);
                      handleStartTimeChange(newDate);
                    }
                  }}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ 
                    step: 300, // 5 min steps
                    min: (() => {
                      // Calculate minimum selectable time based on advance booking requirements
                      if (!selectedDate || !marinaSettingsState) return undefined;
                      
                      const now = new Date();
                      const isToday = selectedDate.toDateString() === now.toDateString();
                      
                      if (isToday) {
                        const minAdvanceHours = (marinaSettingsState as any)?.minAdvanceBooking || 2;
                        const minBookingTime = new Date();
                        minBookingTime.setHours(minBookingTime.getHours() + minAdvanceHours);
                        
                        // Use the later of opening time or minimum booking time
                        const [openHour, openMinute] = currentDayHours.openTime.split(':').map(Number);
                        const openTime = new Date(selectedDate);
                        openTime.setHours(openHour, openMinute, 0, 0);
                        
                        const effectiveMinTime = minBookingTime > openTime ? minBookingTime : openTime;
                        return `${effectiveMinTime.getHours().toString().padStart(2, '0')}:${effectiveMinTime.getMinutes().toString().padStart(2, '0')}`;
                      } else {
                        // For future dates, use opening time
                        return currentDayHours.openTime;
                      }
                    })(),
                    max: (() => {
                      // Calculate maximum selectable time (closing time minus minimum duration)
                      if (!currentDayHours.closeTime) return undefined;
                      
                      const [closeHour, closeMinute] = currentDayHours.closeTime.split(':').map(Number);
                      const closeTime = new Date();
                      closeTime.setHours(closeHour, closeMinute, 0, 0);
                      
                      // Subtract minimum duration to ensure booking doesn't extend past closing
                      const minDuration = marinaSettingsState?.reservationRules?.minReservationDuration || 1;
                      const maxStartTime = new Date(closeTime);
                      maxStartTime.setHours(maxStartTime.getHours() - minDuration);
                      
                      return `${maxStartTime.getHours().toString().padStart(2, '0')}:${maxStartTime.getMinutes().toString().padStart(2, '0')}`;
                    })()
                  }}
                />
              </Box>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="duration-label">Duration (Hours)</InputLabel>
                <Select
                  labelId="duration-label"
                  value={selectedDuration}
                  onChange={handleDurationChange}
                  label="Duration (Hours)"
                  displayEmpty
                >
                  {durationOptions.map(hours => {
                    const priceDetails = calculatePriceForDuration(hours);
                    const totalPrice = priceDetails?.totalPrice ? Number(priceDetails.totalPrice) : 0;
                    return (
                      <MenuItem key={hours} value={hours}>
                        {hours} {hours === 1 ? 'hour' : 'hours'} - ${totalPrice.toFixed(2)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              
              <Button
                variant="contained"
                color="primary"
                onClick={handleCustomTimeSlotConfirm}
                disabled={!selectedStartTime}
                fullWidth
              >
                Check Availability
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, mb: 2, backgroundColor: 'primary.light', color: 'white' }}>
                <Typography variant="subtitle1" gutterBottom>
                  Reservation Summary
                </Typography>
                <Typography variant="body1">
                  Date: {format(selectedDate, 'MMMM d, yyyy')}
                </Typography>
                <Typography variant="body1">
                  Time: {selectedStartTime ? format(selectedStartTime, 'h:mm a') : '--:--'} - {calculateEndTime() ? format(calculateEndTime() as Date, 'h:mm a') : '--:--'}
                </Typography>
                <Typography variant="body1">
                  Duration: {selectedDuration} {selectedDuration === 1 ? 'hour' : 'hours'}
                </Typography>
                {currentPriceDetails && (
                  <Typography variant="body1">
                    Reservation Total: ${Number(currentPriceDetails.totalPrice).toFixed(2)} +Fuel and Tax
                  </Typography>
                )}
                <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                  Business hours: {convertTo12HourFormat(currentDayHours.openTime)} - {convertTo12HourFormat(currentDayHours.closeTime)}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              variant="outlined" 
              onClick={() => setShowTimeSlots(false)}
            >
              Back to Calendar
            </Button>
          </Box>
        </Box>
      </LocalizationProvider>
    );
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mt: 2, mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        showTimeSlots ? renderTimeSlots() : renderCalendar()
      )}
    </Box>
  );
};

export default AvailabilityCalendar; 